/**
 * VAD Module: Handles real-time voice activity detection.
 * Bypasses Webpack parsing errors by loading the library and dependencies from a CDN.
 */
class VADDetector {
    constructor() {
        this.vad = null
        this.isSpeechActive = false
        this.onSpeechStart = null
        this.onSpeechEnd = null
        this.isLoading = false
    }

    /**
     * Dynamically loads required scripts from CDN.
     */
    async loadDependencies() {
        if (window.vad && window.ort) return { vad: window.vad, ort: window.ort }

        const loadScript = (src) => new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = src
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
        })

        try {
            console.log('VAD: Loading dependencies from CDN...')
            // 1. Load ONNX Runtime (required by VAD)
            if (!window.ort) {
                await loadScript('https://cdn.jsdelivr.net/npm/onnxruntime-web@1.14.0/dist/ort.min.js')
            }
            // 2. Load VAD bundle
            if (!window.vad) {
                await loadScript('https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.18/dist/bundle.min.js')
            }
            console.log('VAD: Dependencies loaded')
            return { vad: window.vad, ort: window.ort }
        } catch (err) {
            console.error('VAD: Dependency load failed', err)
            throw err
        }
    }

    /**
     * Initializes the VAD instance with speech start/end callbacks.
     */
    async initializeVAD(callbacks = {}) {
        if (this.isLoading) return
        try {
            this.isLoading = true
            this.onSpeechStart = callbacks.onSpeechStart
            this.onSpeechEnd = callbacks.onSpeechEnd

            const { vad } = await this.loadDependencies()

            // Explicitly configure URLs to avoid 404s in the browser
            // Use local paths for offline support
            const modelURL = '/models/silero_vad.onnx'
            const workletURL = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.18/dist/vad.worklet.bundle.min.js'
            
            this.vad = await vad.MicVAD.new({
                onSpeechStart: () => {
                    this.isSpeechActive = true
                    console.log('VAD: Speech started')
                    if (this.onSpeechStart) this.onSpeechStart()
                },
                onSpeechEnd: () => {
                    this.isSpeechActive = false
                    console.log('VAD: Speech ended')
                    if (this.onSpeechEnd) this.onSpeechEnd()
                },
                // Pass explicit URLs for the model and worklet
                modelURL,
                workletURL,
                positiveSpeechThreshold: 0.8,
                negativeSpeechThreshold: 0.65,
                minSpeechFrames: 3,
                redemptionFrames: 8,
            })

            console.log('VAD: Initialized successfully')
        } catch (e) {
            console.error('VAD: Initialization failed', e)
        } finally {
            this.isLoading = false
        }
    }

    /**
     * Starts the VAD listener.
     */
    startVAD() {
        if (this.vad) {
            this.vad.start()
            console.log('VAD: Listener started')
        }
    }

    /**
     * Stops the VAD listener.
     */
    stopVAD() {
        if (this.vad) {
            this.vad.pause()
            this.isSpeechActive = false
            console.log('VAD: Listener paused')
        }
    }

    /**
     * Returns current speech detection state.
     */
    isSpeechDetected() {
        return this.isSpeechActive
    }
}

export const vadDetector = new VADDetector()
